import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, X, Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { uploadImage, IMAGE_ACCEPT } from "@/lib/upload";
import { useCurrency, CCY_SYMBOLS } from "@/context/CurrencyContext";
import CommodityIcon from "@/components/CommodityIcon";
import SuccessCelebration from "@/components/SuccessCelebration";

const COMMODITIES = [
  "Crude Oil", "Natural Gas", "Gold", "Copper", "Iron Ore", "Coal", "Silver", "Lithium",
  "Maize", "Wheat", "Soybeans", "Coffee", "Sugar", "Platinum", "Chrome", "Vanadium",
  "Nickel", "Palladium", "Rice", "Salt", "Sunflower Oil", "Diamonds", "Bauxite", "Aluminium", "Manganese",
];
const MAX_BYTES = 10 * 1024 * 1024;

export default function PostListing() {
  const nav = useNavigate();
  const { editId } = useParams();
  const { supported, convert } = useCurrency();
  const fileRef = useRef();
  const [f, setF] = useState({ title: "", commodity: "Gold", price: "", currency: "USD", quantity: "", location: "", description: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [photoError, setPhotoError] = useState("");

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${editId}`);
        const l = data.listing;
        setF({ title: l.title, commodity: l.commodity, price: String(l.price), currency: l.currency || "USD",
               quantity: l.quantity || "", location: l.location || "", description: l.description || "" });
        setImages(l.images || []);
      } catch { toast.error("Could not load listing"); }
    })();
  }, [editId]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const errs = [];
    const ok = files.filter((file) => {
      if (!file.type.startsWith("image/")) { errs.push(`"${file.name}" is not an image`); return false; }
      if (file.size > MAX_BYTES) { errs.push(`"${file.name}" is ${(file.size / 1048576).toFixed(1)}MB — each photo must be under 10MB`); return false; }
      return true;
    });
    setPhotoError(errs.join(" · "));
    errs.forEach((m) => toast.error(m));
    if (!ok.length) return;
    setUploading(true);
    for (const file of ok) {
      try { const { url } = await uploadImage(file); setImages((im) => [...im, url]); }
      catch (err) { toast.error(err.response?.data?.detail || `Upload failed for "${file.name}"`); }
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!f.title.trim() || f.title.trim().length < 3) return toast.error("Title must be at least 3 characters");
    if (!f.price || Number(f.price) <= 0) return toast.error("Enter a valid price");
    if (images.length < 2) {
      setPhotoError(`Add at least 2 photos — you have ${images.length}`);
      return toast.error("Please add at least 2 photos to your listing");
    }
    setSubmitting(true);
    try {
      const payload = { ...f, price: Number(f.price), images };
      if (editId) await api.put(`/listings/${editId}`, payload);
      else await api.post("/listings", payload);
      try { navigator.vibrate?.(20); } catch {}
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish");
      setSubmitting(false);
    }
  };

  const priceNum = Number(f.price) || 0;
  const others = supported.filter((c) => c !== f.currency).slice(0, 3);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <SuccessCelebration show={success} title={editId ? "Listing Updated!" : "Listing Published!"}
        subtitle="Your commodity is now live on the marketplace"
        onDone={() => nav("/profile")} />

      <div className="sticky top-0 z-30 glass flex items-center gap-3 px-4 pb-3 pt-4">
        <button onClick={() => nav(-1)} data-testid="post-back" className="pressable glass flex h-10 w-10 items-center justify-center rounded-full">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display text-2xl font-extrabold">{editId ? "Edit Listing" : "Post Listing"}</h1>
      </div>

      <div className="space-y-5 p-4">
        {/* Images */}
        <div>
          <Label>Photos <span className="accent">*</span></Label>
          <div className="no-scrollbar flex gap-3 overflow-x-auto pb-1">
            <button onClick={() => fileRef.current?.click()} data-testid="add-image"
                    className="pressable glass flex h-24 w-24 shrink-0 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed"
                    style={{ borderColor: "var(--accent)" }}>
              {uploading ? <Loader2 className="animate-spin accent" size={22} /> : <Camera size={22} className="accent" />}
              <span className="text-[11px] font-bold accent">Add</span>
            </button>
            {images.map((img, i) => (
              <motion.div key={i} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                          className="relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl">
                <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => setImages((im) => im.filter((_, x) => x !== i))} data-testid={`rm-image-${i}`}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white">
                  <X size={13} />
                </button>
              </motion.div>
            ))}
          </div>
          <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} multiple hidden onChange={onFiles} data-testid="image-input" />
          <p className="mt-1.5 text-xs" data-testid="photo-count"
             style={{ color: images.length >= 2 ? "var(--green)" : "var(--text-dim)" }}>
            {images.length}/2 minimum photos · max 10MB each · images only
          </p>
          {photoError && <p className="mt-1 text-xs font-semibold" style={{ color: "var(--red)" }} data-testid="photo-error">{photoError}</p>}
        </div>

        <Input label="Title *" value={f.title} onChange={set("title")} placeholder="e.g. Premium Chrome Ore — 60% Cr2O3" testid="post-title" maxLength={100} />

        {/* Commodity picker */}
        <div>
          <Label>Commodity Type</Label>
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
            {COMMODITIES.map((c) => (
              <button key={c} onClick={() => setF((p) => ({ ...p, commodity: c }))} data-active={f.commodity === c} data-testid={`commodity-${c}`}
                      className="chip pressable flex shrink-0 items-center gap-2 py-1.5 pl-1.5 pr-3.5 text-sm font-semibold">
                <CommodityIcon name={c} size={14} rounded={10} />{c}
              </button>
            ))}
          </div>
        </div>

        {/* Price + currency */}
        <div>
          <Label>Price</Label>
          <div className="glass flex items-center gap-2 rounded-2xl px-4 py-3.5">
            <span className="text-lg font-bold text-dim">{CCY_SYMBOLS[f.currency]}</span>
            <input type="number" min="0" step="0.01" value={f.price} onChange={set("price")} placeholder="0.00" data-testid="post-price"
                   className="w-full bg-transparent text-lg font-bold outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
          </div>
          <p className="mb-2 mt-3 px-1 text-xs font-bold uppercase tracking-wide text-dim">Post in currency</p>
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
            {supported.map((c) => (
              <button key={c} type="button" onClick={() => setF((p) => ({ ...p, currency: c }))}
                      data-active={f.currency === c} data-testid={`post-ccy-${c}`}
                      className="chip pressable shrink-0 px-4 py-2 text-sm font-bold">
                <span className="mr-1">{CCY_SYMBOLS[c]}</span>{c}
              </button>
            ))}
          </div>
          {priceNum > 0 && others.length > 0 && (
            <motion.p key={f.currency + priceNum} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-2 px-1 text-xs text-dim">
              Buyers see ≈ {others.map((c) => `${CCY_SYMBOLS[c]}${convert(priceNum, f.currency, c).toLocaleString(undefined, { maximumFractionDigits: 2 })} in ${c}`).join(" · ")}
            </motion.p>
          )}
        </div>

        <Input label="Quantity" value={f.quantity} onChange={set("quantity")} placeholder="e.g. 500 tons" testid="post-quantity" />
        <Input label="Location" value={f.location} onChange={set("location")} placeholder="e.g. Accra, Ghana" testid="post-location" />

        <div>
          <Label>Description</Label>
          <textarea value={f.description} onChange={set("description")} rows={4} maxLength={2000} data-testid="post-description"
                    placeholder="Grade, specifications, delivery terms…"
                    className="glass w-full rounded-2xl px-4 py-3 text-sm outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
        </div>

        <motion.button whileTap={{ scale: 0.97 }} onClick={submit} disabled={submitting} data-testid="post-submit"
                       className="btn-accent flex w-full items-center justify-center gap-2 py-4 text-base font-extrabold">
          {submitting ? <Loader2 className="animate-spin" size={20} /> : <><Rocket size={20} /> {editId ? "Update Listing" : "Publish Listing"}</>}
        </motion.button>
      </div>
    </motion.div>
  );
}

const Label = ({ children }) => <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-dim">{children}</p>;

function Input({ label, testid, ...props }) {
  return (
    <div>
      <Label>{label}</Label>
      <input {...props} data-testid={testid}
             className="glass w-full rounded-2xl px-4 py-3.5 text-[15px] outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
    </div>
  );
}
